import { selectIElementIdToColorMap } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  blue,
  FaroButton,
  FaroSlider,
  FaroText,
  grey,
} from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { Box, Stack } from "@mui/system";
import { useMemo, type JSX } from "react";
import {
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from "recharts";
import {
  pointOpacityCurve,
  usePointOpacitySettingsContext,
} from "../point-opacity-settings";
import { DebugOverlaySection } from "./debug-overlay-section";
import { useRenderingStatsContext } from "./rendering-stats-context";

/** @returns Debug view about the calculation of point opacity with dynamic opacity rendering. */
export function PointOpacityView(): JSX.Element {
  return (
    <DebugOverlaySection title="Point Opacity">
      <Stack sx={{ gap: 2 }}>
        <PointOpacityControls />
        <PointOpacityVisualization />
      </Stack>
    </DebugOverlaySection>
  );
}

const maxConfigurablePointDensity = 30;
const maxConfigurableOpacity = 0.6;

function PointOpacityControls(): JSX.Element {
  const {
    minOpacity,
    setMinOpacity,
    maxOpacity,
    setMaxOpacity,
    midPoint,
    setMidPoint,
    growthRate,
    setGrowthRate,
    resetToDefault,
  } = usePointOpacitySettingsContext();

  return (
    <Stack>
      <FaroText variant="bodyS" dark>
        Opacity range
      </FaroText>
      <FaroSlider
        dark
        value={[minOpacity, maxOpacity]}
        min={0.0}
        max={maxConfigurableOpacity}
        step={0.001}
        valueLabelDisplay="auto"
        size="small"
        onChange={(_event, value) => {
          assert(
            Array.isArray(value) && value.length === 2,
            "Range slider should return two values",
          );
          const [value1, value2] = value;

          setMinOpacity(Math.min(value1, value2));
          setMaxOpacity(Math.max(value1, value2));
        }}
      />

      <FaroText variant="bodyS" dark>
        Growth rate
      </FaroText>
      <FaroSlider
        dark
        value={growthRate}
        min={-1.0}
        max={-0.2}
        step={0.01}
        valueLabelDisplay="auto"
        size="small"
        onChange={(_event, value) => {
          assert(typeof value === "number", "Growth rate should be a number");

          setGrowthRate(value);
        }}
      />

      <FaroText variant="bodyS" dark>
        Midpoint
      </FaroText>
      <FaroSlider
        dark
        min={0}
        max={maxConfigurablePointDensity}
        step={0.1}
        shiftStep={0.1}
        value={midPoint}
        valueLabelDisplay="auto"
        size="small"
        onChange={(_event, value) => {
          assert(typeof value === "number", "Midpoint should be a number");

          setMidPoint(value);
        }}
      />
      <FaroButton dark variant="ghost" size="s" onClick={resetToDefault}>
        Reset to Default
      </FaroButton>
    </Stack>
  );
}

const sampleCount = 70;

function PointOpacityVisualization(): JSX.Element {
  const pointOpacitySettings = usePointOpacitySettingsContext();
  const opacityFn = useMemo(
    () => pointOpacityCurve(pointOpacitySettings),
    [pointOpacitySettings],
  );
  const colorMap = useAppSelector(selectIElementIdToColorMap);
  const { pointOpacityMap } = useRenderingStatsContext();

  const opacityData = useMemo(() => {
    const samples = new Array(sampleCount);
    for (let i = 0; i < sampleCount; i++) {
      const pointDensity =
        (i / (sampleCount - 1)) * maxConfigurablePointDensity;
      const opacity = opacityFn(pointDensity);
      samples[i] = {
        pointDensity,
        opacity,
      };
    }
    return samples;
  }, [opacityFn]);

  return (
    <Stack sx={{ justifyContent: "center" }}>
      <LineChart
        width={300}
        height={150}
        data={opacityData}
        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
      >
        <XAxis
          dataKey="pointDensity"
          type="number"
          label={{ value: "points / px", style: { fill: grey[400] } }}
          stroke={grey[400]}
          ticks={[0.0, maxConfigurablePointDensity]}
        />
        <YAxis
          dataKey="opacity"
          type="number"
          stroke={grey[400]}
          domain={[0.0, maxConfigurableOpacity]}
          ticks={[0.0, maxConfigurableOpacity]}
          width={30}
        />
        <ReferenceLine y={pointOpacitySettings.minOpacity} stroke={grey[400]} />
        <ReferenceLine y={pointOpacitySettings.maxOpacity} stroke={grey[400]} />
        <ReferenceLine x={pointOpacitySettings.midPoint} stroke={grey[400]} />
        <Line
          dataKey="opacity"
          stroke={blue[400]}
          dot={false}
          strokeWidth={2}
        />
        {/* Current point densities */}
        {Object.entries(pointOpacityMap).map(([id, stats]) =>
          stats.pointsPerPixel ? (
            <ReferenceLine
              key={`line-${id}`}
              // Ensure it fits in the chart
              x={Math.min(stats.pointsPerPixel, maxConfigurablePointDensity)}
              stroke={colorMap[id]}
            />
          ) : null,
        )}
        {Object.entries(pointOpacityMap).map(([id, stats]) =>
          stats.pointsPerPixel ? (
            <ReferenceDot
              key={`dot-${id}`}
              // Ensure it fits in the chart
              x={Math.min(stats.pointsPerPixel, maxConfigurablePointDensity)}
              y={stats.opacity}
              fill={colorMap[id]}
              stroke="none"
              r={2}
            />
          ) : null,
        )}
        <Tooltip content={CustomTooltip} />
      </LineChart>
    </Stack>
  );
}

function CustomTooltip({
  active,
  payload,
}: TooltipContentProps): JSX.Element | null {
  if (!active || payload.length === 0) {
    return null;
  }

  const { pointDensity, opacity } = payload[0].payload;

  return (
    <Box
      component="div"
      sx={{
        background: grey[1000],
        padding: 1,
        borderRadius: 2,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ justifyContent: "space-between" }}
      >
        <FaroText variant="bodyS" dark>
          points / px
        </FaroText>
        <FaroText variant="bodyS" dark>
          {pointDensity.toFixed(2)}
        </FaroText>
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ justifyContent: "space-between" }}
      >
        <FaroText variant="bodyS" dark>
          opacity
        </FaroText>
        <FaroText variant="bodyS" dark>
          {opacity.toFixed(2)}
        </FaroText>
      </Stack>
    </Box>
  );
}
